import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import PropTypes from "prop-types";

import IconButton from "../../../../../packages/IconButton";
import WPButton from "../../../../../packages/WPButton";
import WPHeading from "../../../../../packages/WPHeading/WPHeading";

import useOutsideClick from "../../../../../hooks/useOutsideClick";

import { SELECTORS } from "./LicenseColumnFilterDropdown.constants";
import {
  calculateDropdownPosition,
  isElementUnderPinnedColumns,
  isElementVisuallyHidden,
} from "./LicenseColumnFilterDropdown.util";

import { FilterIcon } from "../../../../../assets/icons";

import CustomRadioCheck from "../../../../../common/styles/components/checkboxs/CustomRadioCheck";

import "./LicenseColumnFilterDropdown.scss";

/**
 * LicenseColumnFilterDropdown - A column filter dropdown component for license management tables
 *
 * Features:
 * - Multi-select and single-select filtering options
 * - Dynamic positioning that avoids viewport edges and pinned columns
 * - Auto-hide when icon becomes hidden behind pinned columns
 * - Portal-based dropdown to avoid z-index issues
 * - CSS custom properties for dynamic styling
 * - Optimized event handling with immediate position updates
 *
 * @param {string} displayName - The display name for the filter
 * @param {Array} options - Array of filter options with id and label
 * @param {Array} selectedValues - Currently selected filter values
 * @param {Function} onFilterChange - Callback when filter selection changes
 * @param {React.Component} filterIcon - Custom filter icon component
 * @param {string} title - Optional title override for the dropdown header
 * @param {boolean} multiSelect - Whether to allow multiple selections
 * @param {string} className - Additional CSS classes for the container
 * @param {string} dropdownClassName - Additional CSS classes for the dropdown
 */

const LicenseColumnFilterDropdown = ({
  displayName,
  options = [],
  selectedValues = [],
  onFilterChange,
  title,
  multiSelect = true,
  className = "",
  dropdownClassName = "",
  closeOnChange = false,
  showFilterIcon = true,
  isFiltered = false,
  id,
}) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState(selectedValues || []);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    maxWidth: 180,
  });
  const [isIconHidden, setIsIconHidden] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (Array.isArray(selectedValues)) {
      setSelectedOptions(selectedValues);
    }
  }, [selectedValues]);

  /**
   * Handles dropdown position and icon visibility updates on open, scroll, and resize.
   */
  useEffect(() => {
    if (!isDropdownOpen) return;
    const updatePosition = () => {
      if (!buttonRef.current) return;
      const isButtonHidden = isElementVisuallyHidden(buttonRef.current);
      const isUnderPinned = isElementUnderPinnedColumns(buttonRef.current);
      const isHidden = isButtonHidden || isUnderPinned;
      setIsIconHidden(isHidden);
      if (isHidden) {
        setIsDropdownOpen(false);
        return;
      }
      setDropdownPosition(calculateDropdownPosition(buttonRef.current));
    };
    updatePosition();
    window.addEventListener("scroll", updatePosition);
    window.addEventListener("resize", updatePosition);
    const tableContainer = buttonRef.current?.closest(
      SELECTORS.SCROLL_CONTAINERS,
    );
    if (tableContainer) {
      tableContainer.addEventListener("scroll", updatePosition);
    }
    return () => {
      window.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
      if (tableContainer) {
        tableContainer.removeEventListener("scroll", updatePosition);
      }
    };
  }, [isDropdownOpen]);

  /**
   * Handles closing dropdown when clicking outside.
   */
  useOutsideClick(dropdownRef, () => setIsDropdownOpen(false), isDropdownOpen);

  /**
   * Handles filter icon click to open/close dropdown and update position.
   */
  const handleFilterClick = useCallback(() => {
    if (!isDropdownOpen && buttonRef.current) {
      setDropdownPosition(calculateDropdownPosition(buttonRef.current));
    }
    setIsDropdownOpen((prevState) => !prevState);
  }, [isDropdownOpen]);

  const handleApplyFilter = useCallback(() => {
    if (onFilterChange) onFilterChange(selectedOptions);
    setIsDropdownOpen(false);
  }, [selectedOptions, onFilterChange]);

  const handleClearFilter = useCallback(() => {
    if (selectedOptions.length === 0) return;
    setSelectedOptions([]);
    if (onFilterChange) onFilterChange([]);
    setIsDropdownOpen(false);
  }, [selectedOptions, onFilterChange]);

  /**
   * Handles option change for filter selection.
   */
  const handleOptionChange = useCallback(
    (optionId, checked) => {
      let updatedOptions;
      if (multiSelect) {
        if (checked && !selectedOptions.includes(optionId)) {
          updatedOptions = [...selectedOptions, optionId];
        } else if (!checked && selectedOptions.includes(optionId)) {
          updatedOptions = selectedOptions.filter((id) => id !== optionId);
        } else {
          updatedOptions = selectedOptions;
        }
      } else {
        updatedOptions = checked ? [optionId] : [];
      }
      if (updatedOptions !== selectedOptions)
        setSelectedOptions(updatedOptions);
      if (closeOnChange && !multiSelect) {
        if (onFilterChange) onFilterChange(updatedOptions);
        setIsDropdownOpen(false);
      }
    },
    [selectedOptions, multiSelect, onFilterChange, closeOnChange],
  );

  const dropdownStyles = useMemo(
    () => ({
      "--dropdown-top": `${dropdownPosition.top}px`,
      "--dropdown-left": `${dropdownPosition.left}px`,
      "--dropdown-max-width": dropdownPosition.maxWidth
        ? `${dropdownPosition.maxWidth}px`
        : "none",
      "--dropdown-visibility": isIconHidden ? "none" : "block",
      "--dropdown-z-index": isIconHidden ? "1" : "10000",
    }),
    [dropdownPosition, isIconHidden],
  );

  const renderFilterOption = useCallback(
    (option) => (
      <div key={option.id} className="license-filter-option">
        <label className="license-filter-option-label" htmlFor={option.id}>
          <div className="license-filter-icon-label">
            <span className="license-filter-option-icon">{option.icon}</span>
            <WPHeading
              type="default"
              secondary
              size="small"
              className="license-filter-option-text"
              text={t(option.label)}
            />
          </div>
          <div>
            <CustomRadioCheck
              type={multiSelect ? "checkbox" : "radio"}
              id={option.dataTestId || option.id}
              name={multiSelect ? option.name : displayName}
              checked={selectedOptions?.includes(option.id)}
              onChange={(event) =>
                handleOptionChange(option.id, event.target.checked)
              }
              customClass="license-custom-radio-check"
            />
          </div>
        </label>
      </div>
    ),
    [multiSelect, displayName, selectedOptions, handleOptionChange],
  );

  const filterOptions = useMemo(
    () => options.map(renderFilterOption),
    [options, renderFilterOption],
  );

  const dropdownContent = (
    <div
      ref={dropdownRef}
      className={`license-portal-dropdown-content license-column-filter-portal-dropdown ${dropdownClassName}`}
      style={dropdownStyles}
    >
      {title && (
        <div className="license-filter-header">
          <WPHeading text={title} size="xSmall" color="var(--du-color-black)" />
        </div>
      )}
      <div className="license-filter-options">{filterOptions}</div>
      <div className={"license-filter-footer"}>
        <WPButton
          id="apply-filter"
          variant="contained"
          type="primary"
          size="small"
          onClick={handleApplyFilter}
          customClass="license-apply-button"
          disabled={selectedOptions.length === 0}
        >
          {t("ValueStrings.apply")}
        </WPButton>
        <WPButton
          id="clear-filter"
          variant="text"
          type="primary"
          size="small"
          onClick={handleClearFilter}
          customClass="license-clear-button license-primary-green-text"
          disabled={selectedOptions.length === 0}
        >
          {t("ValueStrings.clear")}
        </WPButton>
      </div>
    </div>
  );

  return (
    <div
      className={`license-column-filter-header-renderer ${className}`}
      id={id}
    >
      <div className="header-text-container">
        <WPHeading
          text={displayName}
          tooltip={displayName}
          size="small"
          wrapContent={true}
          color="var(--du-color-white)"
        />
      </div>
      {showFilterIcon && (
        <div ref={buttonRef} className="license-filter-icon-container">
          <IconButton
            id={`${id || "license"}-filter`}
            onClick={handleFilterClick}
            className="filter-icon-container"
            ariaLabel="Filter options"
            tooltip={t("ValueStrings.filter")}
            placement="top"
          >
            <FilterIcon width={14} height={14} />
            {isFiltered && (
              <span
                className="license-filter-badge"
                aria-label="Filter applied"
              />
            )}
          </IconButton>
        </div>
      )}
      {isDropdownOpen && ReactDOM.createPortal(dropdownContent, document.body)}
    </div>
  );
};

LicenseColumnFilterDropdown.propTypes = {
  displayName: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      name: PropTypes.string,
    }),
  ),
  selectedValues: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  onFilterChange: PropTypes.func,
  title: PropTypes.string,
  multiSelect: PropTypes.bool,
  className: PropTypes.string,
  dropdownClassName: PropTypes.string,
  closeOnChange: PropTypes.bool,
  showFilterIcon: PropTypes.bool,
  isFiltered: PropTypes.bool,
};

export default React.memo(LicenseColumnFilterDropdown);
